import { Trans } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { EnvironmentBadge } from "~/components/environment-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { PasswordInput } from "~/components/ui/password-input";
import { useAuthLoginForm } from "~/forms/auth/login";
import { cn } from "~/lib/utils";

export function LoginForm({
	className,
	hintEmail,
	...props
}: React.ComponentPropsWithoutRef<"div"> & {
	hintEmail?: string;
}) {
	const form = useAuthLoginForm({
		defaultValues: {
			email: hintEmail,
		},
	});

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card>
				<CardHeader className="text-center">
					<CardTitle className="text-xl">
						<Trans>Welcome back</Trans>
					</CardTitle>
					<CardDescription>
						<Trans>Login with your Azerion Podcast account</Trans>
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form.AppForm>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								form.handleSubmit();
							}}
						>
							<div className="grid gap-6">
								<form.FormErrorMessages />
								<div className="grid gap-6">
									<form.AppField
										name="email"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Email</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput
														placeholder="podcast@azerion.com"
														autoComplete="email"
														type="email"
													/>
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<form.AppField
										name="password"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<div className="flex items-center">
													<field.FormLabel>
														<Trans>Password</Trans>
													</field.FormLabel>

													<Link
														className="ml-auto text-sm underline-offset-4 hover:underline"
														to="/auth/forgot-password"
													>
														<Trans>Forgot your password?</Trans>
													</Link>
												</div>
												<field.FormControl>
													<field.FormInput asChild>
														<PasswordInput autoComplete="current-password" />
													</field.FormInput>
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<form.FormButton>
										<Trans>Sign in</Trans>
									</form.FormButton>
								</div>
								<div className="text-center text-sm">
									<Trans>Don&apos;t have an account?</Trans>{" "}
									<Link className="underline underline-offset-4" to="/auth/sign-up">
										<Trans>Sign up</Trans>
									</Link>
								</div>
							</div>
						</form>
					</form.AppForm>
				</CardContent>
			</Card>
			<div className="text-muted-foreground [&_a]:hover:text-primary text-center text-xs text-balance [&_a]:underline [&_a]:underline-offset-4">
				<Trans>
					By clicking continue, you agree to our <a href="https://azerion.com">Terms of Service</a>{" "}
					and <a href="https://azerion.com">Privacy Policy</a>.
				</Trans>
			</div>
			<div className="text-center">
				<EnvironmentBadge />
			</div>
		</div>
	);
}
